import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '@/api/client'

export interface AvailableBonus {
  id: number
  type: string
  name: string
  description: string | null
  bonus_amount: number | null
  percentage: number | null
  wagering_multiplier: number
  max_cashout: number | null
  expires_at: string | null
  eligible: boolean
}

export interface ActiveBonus {
  id: number
  bonus_id: number
  bonus_name: string
  bonus_amount: number
  wagering_required: string
  wagering_completed: string
  progress_percent: number
  expires_at: string | null
  max_bet: number | null
  status: string
}

export interface BonusHistoryItem {
  id: number
  bonus_id: number
  bonus_name: string
  type: string
  bonus_amount: number
  wagering_required: string
  wagering_completed: string
  status: string
  activated_at: string | null
}

export interface BonusHistoryMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export interface ClaimedBonus {
  id: number
  bonus_id: number
  bonus_name: string
  bonus_amount: number
  wagering_required: string
  expires_at: string | null
  status: string
}

export const useBonusStore = defineStore('bonus', () => {
  // State
  const availableBonuses = ref<AvailableBonus[]>([])
  const activeBonuses = ref<ActiveBonus[]>([])
  const bonusHistory = ref<BonusHistoryItem[]>([])
  const historyMeta = ref<BonusHistoryMeta>({ current_page: 1, last_page: 1, per_page: 10, total: 0 })

  // Loading flags
  const loadingAvailable = ref(false)
  const loadingActive = ref(false)
  const loadingHistory = ref(false)
  const loadingClaim = ref(false)
  const loadingForfeit = ref(false)
  const loadingRedeem = ref(false)

  // Error state
  const error = ref<string | null>(null)

  async function fetchAvailable(): Promise<void> {
    loadingAvailable.value = true
    error.value = null

    try {
      const response = await apiClient.get('/api/bonuses')
      availableBonuses.value = response.data
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to load available bonuses'
      throw err
    } finally {
      loadingAvailable.value = false
    }
  }

  async function fetchActive(): Promise<void> {
    loadingActive.value = true
    error.value = null

    try {
      const response = await apiClient.get('/api/bonuses/active')
      activeBonuses.value = response.data
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to load active bonuses'
      throw err
    } finally {
      loadingActive.value = false
    }
  }

  async function fetchHistory(page = 1): Promise<void> {
    loadingHistory.value = true
    error.value = null

    try {
      const response = await apiClient.get('/api/bonuses/history', { params: { page } })
      bonusHistory.value = response.data.data
      historyMeta.value = {
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to load bonus history'
      throw err
    } finally {
      loadingHistory.value = false
    }
  }

  async function claimBonus(id: number): Promise<ClaimedBonus> {
    loadingClaim.value = true
    error.value = null

    try {
      const response = await apiClient.post(`/api/bonuses/${id}/claim`)
      return response.data as ClaimedBonus
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to claim bonus'
      throw err
    } finally {
      loadingClaim.value = false
    }
  }

  async function forfeitBonus(userBonusId: number): Promise<void> {
    loadingForfeit.value = true
    error.value = null

    try {
      await apiClient.post(`/api/bonuses/${userBonusId}/forfeit`)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to forfeit bonus'
      throw err
    } finally {
      loadingForfeit.value = false
    }
  }

  async function redeemCode(code: string): Promise<ClaimedBonus> {
    loadingRedeem.value = true
    error.value = null

    try {
      const response = await apiClient.post('/api/bonuses/redeem-code', { code })
      return response.data as ClaimedBonus
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to redeem promo code'
      throw err
    } finally {
      loadingRedeem.value = false
    }
  }

  return {
    availableBonuses,
    activeBonuses,
    bonusHistory,
    historyMeta,
    loadingAvailable,
    loadingActive,
    loadingHistory,
    loadingClaim,
    loadingForfeit,
    loadingRedeem,
    error,
    fetchAvailable,
    fetchActive,
    fetchHistory,
    claimBonus,
    forfeitBonus,
    redeemCode,
  }
})
